/**
 * @author Ka Wing Chin <kawing.chin@takeaway.com>
 * @class Takeaway.Product
 * The product that are placed in the basket.
 * To make it even better, there could be an entity for abstract products.
 */
import ENUM from '../lib/enums'
import Utils from '../lib/utils'

export default class Product {
  constructor (obj) {
    if (obj !== undefined) {
      for (let prop in obj) {
        this[prop] = obj[prop]
      }
    }
  }

  /**
   * Maximum length for the comment.
   */
  static CommentMaxLength = 160

  uniqueId
  id
  count = 0
  productName
  comment = null
  categoryId = null
  deliveryType
  /**
   * In the database is called is_meal which is by default true. In the parser the boolean is reverted.
   * It should make more sense if database returned is_not_meal and default would be false.
   * @property {boolean} ignoreMinimumOrderValue
   * @default false
   */
  ignoreMinimumOrderValue = false
  /**
   * Single delivery price without sidedish.
   */
  basePrice = 0
  /**
   * Single delivery price with sidedish.
   */
  price = 0
  /**
   * Base total delivery price without sidedish.
   */
  baseTotalPrice = 0
  /**
   * Total delivery price with sidedish.
   */
  totalPrice = 0
  /**
   * Single pickup price without sidedish.
   */
  basePricePickup = 0
  /**
   * Single pickup price with sidedish.
   */
  pricePickup = 0
  /**
   * Total pickup price without sidedish.
   */
  baseTotalPricePickup = 0
  /**
   * Total pickup price with sidedish.
   */
  totalPricePickup = 0
  /**
   * Total price with minimum order value.
   */
  totalPriceMinimumOrder = 0

  /**
   * @property ProductSize
   */
  size = null
  /**
   * @property {boolean} Wether the product is disabled. Used for unavailibility
   */
  isDisabled = false
  /**
   * @property {int} comboCounter A helper for the combination discount.
   * It tracks how many times the product can be used for a combination.
   */
  comboCounter

  /**
   * Update the total price of the base and side dish included.
   */
  updateTotalPrice () {
    if (this.size === null) {
      this.price = this.basePrice
      this.pricePickup = this.basePricePickup
      if (!this.ignoreMinimumOrderValue) {
        this.totalPriceMinimumOrder = this.basePrice
      }
    } else {
      this.price = this.size.price
      this.pricePickup = this.size.pricePickup

      if (!this.ignoreMinimumOrderValue) {
        this.totalPriceMinimumOrder = this.price
      }

      for (let x in this.size.sideDishes) {
        const sideDish = this.size.sideDishes[x]
        this.price += sideDish.price
        this.pricePickup += sideDish.pricePickup
        if (!sideDish.ignoreMinimumOrderValue) {
          this.totalPriceMinimumOrder += sideDish.price
        }
      }
    }

    // Round correctly.
    this.price = Utils.toMoney(this.price, 2)
    this.pricePickup = Utils.toMoney(this.pricePickup, 2)

    // TODO I think it is better do all the calculation in a getter.

    // Base total price
    this.baseTotalPrice = Utils.toMoney(parseFloat((this.basePrice * this.count)), 2)
    this.baseTotalPricePickup = Utils.toMoney(parseFloat((this.basePricePickup * this.count)), 2)

    // Total price included sidedishes
    this.totalPrice = Utils.toMoney(parseFloat((this.price * this.count)), 2)
    this.totalPricePickup = Utils.toMoney(parseFloat((this.pricePickup * this.count)), 2)

    // console.log(`${this.baseTotalPrice} : ${this.totalPrice}`);
    // console.log(`${this.baseTotalPricePickup} : ${this.totalPricePickup}`);
  }

  /**
   * Base price without sidedish.
   * @param {Takeaway.ENUM.DeliveryMethod.Type} deliveryType
   * @return {float}
   */
  getBasePrice (deliveryType) {
    // When a size is changed change the Baseprice of sizes
    if (deliveryType === ENUM.DeliveryMethod.Type.Delivery) {
      if (this.size && this.size.id) {
        return this.size.price
      }
      return this.basePrice
    } else if (deliveryType === ENUM.DeliveryMethod.Type.Pickup) {
      if (this.size && this.size.id) {
        return this.size.pricePickup
      }
      return this.basePricePickup
    }
    throw new Error('Wrong DeliveryType: ' + deliveryType + '. Use Takeaway.DeliveryMethod.Type')
  }

  /**
   * returns sizeId if exists otherwise it returns the productId
   * @returns {id}
   */
  getId () {
    if (this.size && this.size.id) {
      return this.size.id
    } else {
      return this.id
    }
  }

  /**
   * lowers the ComboCounter of the product.
   * ComboCounter is used to for checking if the product can still be used for a discount
   * (after a reset it is the same as the count property)
   * @returns {boolean} - if it lowered the counter it returns true
   */
  lowerComboCounter () {
    if (this.comboCounter) {
      this.comboCounter = this.comboCounter - 1
      return true
    }
  }

  /**
   * Price with sidedish.
   * @param {Takeaway.ENUM.DeliveryMethod.Type} deliveryType
   * @return {float}
   */
  getPrice (deliveryType) {
    if (deliveryType === ENUM.DeliveryMethod.Type.Delivery) {
      return this.price
    } else if (deliveryType === ENUM.DeliveryMethod.Type.Pickup) {
      return this.pricePickup
    }

    throw new Error('Wrong DeliveryType: ' + deliveryType + '. Use Takeaway.ENUM.DeliveryMethod.Type')
  }

  /**
   * Base total price without sidedish.
   * @param {Takeaway.ENUM.DeliveryMethod.Type} deliveryType
   * @return {float}
   */
  getBaseTotalPrice (deliveryType) {
    if (deliveryType === ENUM.DeliveryMethod.Type.Delivery) {
      return this.baseTotalPrice
    } else if (deliveryType === ENUM.DeliveryMethod.Type.Pickup) {
      return this.baseTotalPricePickup
    }

    throw new Error('Wrong DeliveryType: ' + deliveryType + '. Use Takeaway.ENUM.DeliveryMethod.Type')
  }

  /**
   * Total price with sidedish.
   * @param {DeliveryMethod} deliveryType
   * @return {float}
   */
  getTotalPrice (deliveryType) {
    if (deliveryType === ENUM.DeliveryMethod.Type.Delivery) {
      return this.totalPrice
    } else if (deliveryType === ENUM.DeliveryMethod.Type.Pickup) {
      return this.totalPricePickup
    }

    throw new Error('Wrong DeliveryType: ' + deliveryType + '. Use Takeaway.ENUM.DeliveryMethod.Type')
  }

  /**
   * Get the MOV with sidedish or not.
   * @param {boolean} inclusiveSidedishes
   * @param {DeliveryMethod} deliveryType
   * @return {float}
   */
  getMinimumOrderValueTotalPrice (inclusiveSidedishes, deliveryType) {
    if (this.ignoreMinimumOrderValue) {
      return 0
    }

    if (deliveryType === ENUM.DeliveryMethod.Type.Delivery) {
      return inclusiveSidedishes
        ? this.totalPriceMinimumOrder
        : this.basePrice
    } else if (deliveryType === ENUM.DeliveryMethod.Type.Pickup) {
      return 0
    }

    throw new Error('Wrong DeliveryType: ' + deliveryType + '. Use Takeaway.ENUM.DeliveryMethod.Type')
  }

  /**
   * A helper for combination calculation.
   * Resets the combination counter.
   */
  resetComboCounter () {
    this.comboCounter = this.count
  }
}
